import type { Action } from "@judo/actions";
import type { Table } from "@judo/model-api";
import type { GridFilterModel, GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import { useCallback, useEffect, useRef, useState } from "react";
import { buildQueryCustomizer } from "../utils/build-query-customizer";
import { buildInitialSortModel } from "../utils/table-sort-utils";

/**
 * Options for useTableData hook.
 */
export interface UseTableDataOptions {
	/** Table visual element */
	element: Table;
	/** Pre-resolved refresh action for non-eager tables (resolved by xmi:id in TableRenderer) */
	refreshAction?: Action;
	/** Raw data from DataContext */
	rawData: any[] | undefined;
	/** Action dispatcher for non-eager mode (optional) */
	dispatch?: ((action: any) => Promise<void>) | null;
	/** Loading state from data store */
	loading?: boolean;
	/** Whether to request total count from backend (for pagination) */
	showTotalCount?: boolean;
	/** Total count from server (for server-side pagination with showTotalCount) */
	totalCount?: number;
	/** Pre-computed mask string to include in query customizers */
	mask?: string;
	/** Whether MUI Pro multi-column sorting is available */
	isProEnabled?: boolean;
}

/**
 * Result type from useTableData hook.
 */
export interface TableDataResult {
	/** Raw data rows (DataGrid handles filtering/sorting/pagination based on mode) */
	rows: any[];
	/** Total count (for server-side pagination) */
	rowCount?: number;
	/** Current pagination state */
	paginationModel: GridPaginationModel;
	/** Current sort state */
	sortModel: GridSortModel;
	/** Current filter state */
	filterModel: GridFilterModel;
	/** Pagination mode: 'client' for eager, 'server' for non-eager */
	paginationMode: "client" | "server";
	/** Sorting mode: 'client' for eager, 'server' for non-eager */
	sortingMode: "client" | "server";
	/** Filter mode: 'client' for eager, 'server' for non-eager */
	filterMode: "client" | "server";
	/** Handle pagination change */
	onPaginationModelChange: (model: GridPaginationModel) => void;
	/** Handle sort change */
	onSortModelChange: (model: GridSortModel) => void;
	/** Handle filter change */
	onFilterModelChange: (model: GridFilterModel) => void;
	/** Loading state */
	loading: boolean;
}

/**
 * Hook for managing table data with support for both eager (client-side)
 * and non-eager (server-side) data management modes.
 *
 * Leverages MUI DataGrid's built-in mode props:
 * - `paginationMode`: 'client' | 'server'
 * - `sortingMode`: 'client' | 'server'
 * - `filterMode`: 'client' | 'server'
 *
 * **Eager Mode** (`element.isEager === true`):
 * - Sets all modes to 'client'
 * - DataGrid handles filtering/sorting/pagination internally
 * - All data passed to DataGrid at once
 * - No backend calls for user interactions
 *
 * **Non-Eager Mode** (`element.isEager === false`):
 * - Sets all modes to 'server'
 * - DataGrid fires events, we dispatch actions
 * - Backend processes all operations and returns results
 *
 * @param element - The Table visual element
 * @param refreshAction - Pre-resolved refresh action for non-eager tables
 * @param rawData - The raw data array from backend/context
 * @returns TableDataResult with mode configuration and event handlers
 */
export function useTableData({
	element,
	refreshAction,
	rawData,
	dispatch,
	loading: externalLoading,
	showTotalCount = false,
	totalCount,
	mask,
	isProEnabled = false,
}: UseTableDataOptions): TableDataResult {
	// Parser now returns rowsPerPage as number, but handle edge cases (0, undefined)
	const initialPageSize = element.rowsPerPage && element.rowsPerPage > 0 ? element.rowsPerPage : 10;

	const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
		page: 0,
		pageSize: initialPageSize,
	});
	const [sortModel, setSortModel] = useState<GridSortModel>(() => buildInitialSortModel(element.columns, isProEnabled));
	const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

	// Determine mode based on isEager flag
	const mode = element.isEager ? ("client" as const) : ("server" as const);

	// Eager table: client mode - DataGrid handles everything
	if (element.isEager) {
		return {
			rows: rawData ?? [],
			// rowCount not needed for client mode - DataGrid calculates from rows
			paginationModel,
			sortModel,
			filterModel,
			paginationMode: mode,
			sortingMode: mode,
			filterMode: mode,
			onPaginationModelChange: setPaginationModel,
			onSortModelChange: setSortModel,
			onFilterModelChange: setFilterModel,
			loading: externalLoading ?? false,
		};
	}

	// Non-eager table: server mode
	// Dispatch refresh action with complete queryCustomizer whenever state changes
	const dispatchRefresh = useCallback(
		(sort: GridSortModel, pagination: GridPaginationModel, filter: GridFilterModel) => {
			if (!dispatch || !refreshAction) return;

			const queryCustomizer = buildQueryCustomizer({
				sortModel: sort,
				paginationModel: pagination,
				filterModel: filter,
				columns: element.columns,
				mask,
			});

			dispatch({
				...refreshAction,
				queryCustomizer,
				// Only pass countRecords if showTotalCount is true (don't send false)
				...(showTotalCount ? { countRecords: true } : {}),
			}).catch((error) => {
				console.error("Failed to dispatch refresh action:", error);
			});
		},
		[refreshAction, dispatch, showTotalCount, element.columns, mask]
	);

	// Track if initial load has been triggered
	const hasInitialLoadRef = useRef(false);

	// Initial data load on mount.
	// DataGrid may fire onSortModelChange synchronously during render (before this effect),
	// but onSortModelChange skips dispatch until hasInitialLoadRef is true.
	// This effect is the single source of truth for the first dispatch.
	// oxlint-disable-next-line react-hooks/exhaustive-deps -- Only run once on mount, using refs for stable callbacks
	useEffect(() => {
		if (hasInitialLoadRef.current) return;
		hasInitialLoadRef.current = true;
		dispatchRefresh(sortModel, paginationModel, filterModel);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Empty deps - only run once on mount

	const onSortModelChange = useCallback(
		(model: GridSortModel) => {
			setSortModel(model);
			// Skip dispatch during DataGrid's initial mount (useEffect handles it).
			// This also prevents duplicate dispatch under React strict mode double-render.
			if (!hasInitialLoadRef.current) return;
			dispatchRefresh(model, paginationModel, filterModel);
		},
		[dispatchRefresh, paginationModel, filterModel]
	);

	const onFilterModelChange = useCallback(
		(model: GridFilterModel) => {
			setFilterModel(model);
			dispatchRefresh(sortModel, paginationModel, model);
		},
		[dispatchRefresh, sortModel, paginationModel]
	);

	const onPaginationModelChange = useCallback(
		(model: GridPaginationModel) => {
			setPaginationModel(model);
			dispatchRefresh(sortModel, model, filterModel);
		},
		[dispatchRefresh, sortModel, filterModel]
	);

	return {
		rows: rawData ?? [],
		// Use totalCount from server if available, otherwise fall back to array length
		rowCount: totalCount ?? rawData?.length ?? 0,
		paginationModel,
		sortModel,
		filterModel,
		paginationMode: mode,
		sortingMode: mode,
		filterMode: mode,
		onPaginationModelChange,
		onSortModelChange,
		onFilterModelChange,
		loading: externalLoading ?? false,
	};
}
